import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { ConfluenceAccumulatedDataType } from './confluence-accumulated-data-type';

/**
 * Data payload for creating Confluence slides.
 */
export type ConfluenceCreateSlidesData = ConfluenceAccumulatedDataType & {
	/**
	 * The title to be included in the created slides.
	 */
	title: string;
	/**
	 * The JSON document to be included in the created slides.
	 */
	document: string;
};

/**
 * Properties for the Confluence slides creation command.
 *
 * @extends CommandProperties
 */
export interface ConfluenceCreateSlidesCommandProperties extends CommandProperties {
	/**
	 * The unique key identifier for the slides creation request.
	 */
	key: string;
	/**
	 * The data payload containing the content for the new slides.
	 */
	data: ConfluenceCreateSlidesData;
	/**
	 * Whether the stream is complete.
	 */
	isStreamComplete: boolean;
	/**
	 * Single instrumentation ID of the request
	 */
	singleInstrumentationID: string | undefined;
}

/**
 * A command sent by a bridge API client to create new Confluence slides.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to create Confluence slides with the specified content.
 */
export const ConfluenceSlidesCreateCommand: CommandConstructor<
	ConfluenceCreateSlidesCommandProperties,
	SerializableValue
> = createCommand<ConfluenceCreateSlidesCommandProperties>('confluence-slides-create-command');
